// Competition Countdown Planner — pure, deterministic week-by-week plan from
// today to a competition date, plus a client-side .ics calendar export.
// Dates are plain ISO "YYYY-MM-DD" strings handled in UTC, so the result never
// depends on the visitor's time zone. Unit tests: tests/competitionPlanner.test.ts.

export type Level = 'beginner' | 'intermediate' | 'advanced';
export type Days = 2 | 3 | 4 | 5;
export const LEVELS: Level[] = ['beginner', 'intermediate', 'advanced'];
export const DAY_OPTIONS: Days[] = [2, 3, 4, 5];

export type Phase = 'build' | 'deload' | 'intensification' | 'peak' | 'taper';
export type SessionKind =
  | 'strengthVolume'
  | 'tableTechnique'
  | 'accessory'
  | 'deload'
  | 'tableLight'
  | 'heavySpecific'
  | 'tableSparring'
  | 'sharpen'
  | 'activation';

export interface PlannerInputs {
  competitionDate: string;
  startDate: string;
  days: Days;
  level: Level;
}

export interface PlannedSession { date: string; kind: SessionKind }
export interface PlannedWeek { index: number; monday: string; phase: Phase; sessions: PlannedSession[] }
export type MilestoneKind = 'start' | 'deload' | 'intensification' | 'weightCheck' | 'lastHeavy' | 'taper' | 'restDays' | 'competition';
export interface Milestone { date: string; kind: MilestoneKind }

export interface CompetitionPlan {
  inputs: PlannerInputs;
  weeks: PlannedWeek[];
  milestones: Milestone[];
  daysUntil: number;
  restDaysBefore: number;
  totalSessions: number;
  /** Less than 4 weeks: there isn't time for a real build, so the plan is a short sharpen-and-taper. */
  shortPrep: boolean;
}

export const MAX_DAYS_AHEAD = 365;

// Same Monday-based layouts as the workout generator.
const WEEK_LAYOUT: Record<Days, number[]> = { 2: [0, 3], 3: [0, 2, 4], 4: [0, 1, 3, 4], 5: [0, 1, 2, 4, 5] };

const SESSIONS_BY_PHASE: Record<Phase, SessionKind[]> = {
  build: ['strengthVolume', 'tableTechnique', 'strengthVolume', 'accessory', 'tableTechnique'],
  deload: ['deload', 'tableLight', 'deload', 'tableLight', 'deload'],
  intensification: ['heavySpecific', 'tableSparring', 'heavySpecific', 'accessory', 'tableSparring'],
  peak: ['sharpen', 'tableSparring', 'sharpen'],
  taper: ['activation', 'activation'],
};

// ---------------------------------------------------------------------------
// Date helpers (UTC)
// ---------------------------------------------------------------------------

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isIsoDate(s: string): boolean {
  const m = s.match(ISO);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
const toMs = (iso: string) => {
  const m = iso.match(ISO)!;
  return Date.UTC(+m[1], +m[2] - 1, +m[3]);
};
const fromMs = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const addDays = (iso: string, n: number) => fromMs(toMs(iso) + n * 86400000);
export const diffDays = (a: string, b: string) => Math.round((toMs(b) - toMs(a)) / 86400000);
/** 0 = Monday … 6 = Sunday. */
export const weekday = (iso: string) => (new Date(toMs(iso)).getUTCDay() + 6) % 7;
export const mondayOf = (iso: string) => addDays(iso, -weekday(iso));

export function todayIso(now = new Date()): string {
  // Local calendar date of the visitor (what "today" means to them).
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export const restDaysBefore = (level: Level) => (level === 'beginner' ? 3 : 2);

/** Phase for each week, given the total number of weeks (last week = competition week). */
export function phasesFor(weeks: number): Phase[] {
  const intens = weeks >= 8 ? 3 : weeks >= 5 ? 2 : weeks >= 4 ? 1 : 0;
  const out: Phase[] = [];
  const buildWeeks = Math.max(0, weeks - 2 - intens);
  for (let b = 0; b < buildWeeks; b++) out.push((b + 1) % 4 === 0 ? 'deload' : 'build');
  // Don't let a deload be the last build week (you'd start intensifying straight
  // off a light week); move it one week earlier.
  if (buildWeeks >= 4 && out[buildWeeks - 1] === 'deload') {
    out[buildWeeks - 1] = 'build';
    out[buildWeeks - 2] = 'deload';
  }
  for (let i = 0; i < intens && out.length < weeks - 2; i++) out.push('intensification');
  if (weeks >= 2) out.push('peak');
  if (weeks >= 1) out.push('taper');
  return out.slice(-weeks);
}

export type PlannerError = 'invalidDate' | 'inPast' | 'tooFar';

export function validate(inputs: Pick<PlannerInputs, 'competitionDate' | 'startDate'>): PlannerError | null {
  if (!isIsoDate(inputs.competitionDate) || !isIsoDate(inputs.startDate)) return 'invalidDate';
  const d = diffDays(inputs.startDate, inputs.competitionDate);
  if (d <= 0) return 'inPast';
  if (d > MAX_DAYS_AHEAD) return 'tooFar';
  return null;
}

export function planCompetition(inputs: PlannerInputs): CompetitionPlan | null {
  if (validate(inputs)) return null;
  const { competitionDate: comp, startDate: start, days, level } = inputs;
  const rest = restDaysBefore(level);
  const lastTrainingDay = addDays(comp, -rest - 1);
  const firstMonday = mondayOf(start);
  const compMonday = mondayOf(comp);
  const nWeeks = diffDays(firstMonday, compMonday) / 7 + 1;
  const phases = phasesFor(nWeeks);

  const weeks: PlannedWeek[] = phases.map((phase, index) => {
    const monday = addDays(firstMonday, index * 7);
    let slots = WEEK_LAYOUT[days];
    if (phase === 'deload') slots = slots.slice(0, Math.max(2, days - 1));
    if (phase === 'peak') slots = slots.slice(0, Math.min(days, 3));
    if (phase === 'taper') slots = slots.slice(0, 2);
    const kinds = SESSIONS_BY_PHASE[phase];
    const sessions: PlannedSession[] = [];
    slots.forEach((off, j) => {
      const date = addDays(monday, off);
      if (diffDays(start, date) < 0) return; // before today
      if (diffDays(date, lastTrainingDay) < 0) return; // inside the pre-competition rest window
      let kind = kinds[j % kinds.length];
      // Beginners don't do match-like table sparring; keep table time technical.
      if (level === 'beginner' && kind === 'tableSparring') kind = 'tableTechnique';
      sessions.push({ date, kind });
    });
    return { index, monday, phase, sessions };
  });

  const all = weeks.flatMap((w) => w.sessions);
  const milestones: Milestone[] = [];
  if (all[0]) milestones.push({ date: all[0].date, kind: 'start' });
  for (const w of weeks) {
    if (w.phase === 'deload' && w.sessions[0]) milestones.push({ date: w.sessions[0].date, kind: 'deload' });
  }
  const firstIntens = weeks.find((w) => w.phase === 'intensification' && w.sessions.length);
  if (firstIntens) milestones.push({ date: firstIntens.sessions[0].date, kind: 'intensification' });
  const weightCheck = addDays(comp, -14);
  if (diffDays(start, weightCheck) >= 0) milestones.push({ date: weightCheck, kind: 'weightCheck' });
  const heavy = all.filter((s) => s.kind === 'heavySpecific' || s.kind === 'sharpen' || s.kind === 'tableSparring');
  if (heavy.length) milestones.push({ date: heavy[heavy.length - 1].date, kind: 'lastHeavy' });
  const taperWeek = weeks.find((w) => w.phase === 'taper');
  if (taperWeek) {
    const taperStart = diffDays(start, taperWeek.monday) >= 0 ? taperWeek.monday : start;
    if (diffDays(taperStart, comp) > 0) milestones.push({ date: taperStart, kind: 'taper' });
  }
  const restStart = addDays(comp, -rest);
  if (diffDays(start, restStart) >= 0) milestones.push({ date: restStart, kind: 'restDays' });
  milestones.push({ date: comp, kind: 'competition' });
  const order: MilestoneKind[] = ['start', 'deload', 'intensification', 'weightCheck', 'lastHeavy', 'taper', 'restDays', 'competition'];
  milestones.sort((a, b) => diffDays(b.date, a.date) || order.indexOf(a.kind) - order.indexOf(b.kind));

  return {
    inputs,
    weeks,
    milestones,
    daysUntil: diffDays(start, comp),
    restDaysBefore: rest,
    totalSessions: all.length,
    shortPrep: nWeeks < 4,
  };
}

// ---------------------------------------------------------------------------
// Share URL
// ---------------------------------------------------------------------------

export function parsePlannerQuery(params: URLSearchParams): Partial<Omit<PlannerInputs, 'startDate'>> {
  const out: Partial<Omit<PlannerInputs, 'startDate'>> = {};
  const date = params.get('date');
  if (date && isIsoDate(date)) out.competitionDate = date;
  const d = Number(params.get('days'));
  if ((DAY_OPTIONS as number[]).includes(d)) out.days = d as Days;
  const l = params.get('level');
  if (l && (LEVELS as string[]).includes(l)) out.level = l as Level;
  return out;
}

export function serializePlannerQuery(i: Omit<PlannerInputs, 'startDate'>): string {
  return new URLSearchParams({ date: i.competitionDate, days: String(i.days), level: i.level }).toString();
}

// ---------------------------------------------------------------------------
// iCalendar (RFC 5545) export: all-day events, CRLF line endings, folded lines.
// ---------------------------------------------------------------------------

export interface IcsEvent { date: string; summary: string; description?: string }

export function icsEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/** Fold a content line to ≤75 octets (UTF-8), continuation lines start with a space. */
export function icsFold(line: string): string {
  const enc = new TextEncoder();
  if (enc.encode(line).length <= 75) return line;
  const parts: string[] = [];
  let cur = '';
  let curLen = 0;
  for (const ch of line) {
    const n = enc.encode(ch).length;
    const limit = parts.length === 0 ? 75 : 74; // continuation lines carry a leading space
    if (curLen + n > limit) {
      parts.push(cur);
      cur = '';
      curLen = 0;
    }
    cur += ch;
    curLen += n;
  }
  parts.push(cur);
  return parts.join('\r\n ');
}

export function buildIcs(opts: { calendarName: string; events: IcsEvent[]; stampDate: string; uidPrefix?: string }): string {
  const stamp = `${opts.stampDate.replace(/-/g, '')}T000000Z`;
  const prefix = opts.uidPrefix || 'armprogress-competition-planner';
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ArmProgress//Competition Planner//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${icsEscape(opts.calendarName)}`,
  ];
  opts.events.forEach((e, i) => {
    const d = e.date.replace(/-/g, '');
    const next = addDays(e.date, 1).replace(/-/g, '');
    lines.push(
      'BEGIN:VEVENT',
      `UID:${prefix}-${d}-${i}@armprogress.mcdevlabs.com`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${d}`,
      `DTEND;VALUE=DATE:${next}`,
      `SUMMARY:${icsEscape(e.summary)}`,
      ...(e.description ? [`DESCRIPTION:${icsEscape(e.description)}`] : []),
      'TRANSP:TRANSPARENT',
      'END:VEVENT',
    );
  });
  lines.push('END:VCALENDAR');
  return lines.map(icsFold).join('\r\n') + '\r\n';
}
